import { Button, Flex, Form, Segmented } from "antd";
import { useEffect, useState } from "react";
import PencilLine from "../../../assets/icons/PencilLine";
import TitleBar from "../../../components/common/TitleBar";
import SidebarLayout from "../../../layouts/SidebarLayout";

// Segment components
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../app/store";
import WithLoader from "../../../components/WithLoader";
import {
  useGetVendorDetailsQuery,
  useUpdateVendorMutation,
} from "../../../features/Vendor/vendor.api";
import {
  setVendor,
  updateContacts,
  updateDocuments,
  updateGeneralInfo,
  updatePayments,
} from "../../../features/Vendor/vendor.slice";
import { VendorDetails as TVendorDetails } from "../../../features/Vendor/vendor.type";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import Contacts from "./_components/ManageVendor/Contacts";
import Documents from "./_components/ManageVendor/Documents";
import General from "./_components/ManageVendor/General";
import Payments from "./_components/ManageVendor/Payments";
import ActivityLog from "./_components/VendorDetails/ActivityLog";
import ContactDetails from "./_components/VendorDetails/ContactDetails";
import DocumentDetails from "./_components/VendorDetails/DocumentDetails";
import GeneralDetails from "./_components/VendorDetails/GeneralDetails";
import PaymentDetails from "./_components/VendorDetails/PaymentDetails";

type SegmentKey =
  | "general"
  | "contacts"
  | "payments"
  | "documents"
  | "activity-log";

const VendorDetails = () => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();

  const [activeSegment, setActiveSegment] = useState<SegmentKey>("general");
  const [isEditMode, setIsEditMode] = useState(false);

  const { data, isLoading, isFetching } = useGetVendorDetailsQuery(
    id as unknown as number,
    {
      skip: !id,
    },
  );
  const { payments, documents, contacts, ...general } = data?.data || {};

  const [
    updateVendor,
    {
      isLoading: updating,
      isSuccess: updated,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateVendorMutation();

  // ------ Function for go to the next step with form validation ------
  const handleAction = async () => {
    try {
      const values = await form.validateFields();
      if (activeSegment === "general") {
        updateVendor({ data: values, id });
        dispatch(updateGeneralInfo({ ...values, id }));
      } else if (activeSegment === "contacts") {
        updateVendor({ data: values, id });
        dispatch(updateContacts({ ...values }));
      } else if (activeSegment === "payments") {
        updateVendor({ data: values, id });
        dispatch(updatePayments({ ...values }));
      } else if (activeSegment === "documents") {
        updateVendor({ data: values, id });
        dispatch(updateDocuments({ ...values }));
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (data?.data) {
      form.setFieldsValue({
        ...data.data,
        paymentInfo: data.data.payments,
      });
      dispatch(setVendor(data.data));
    }
  }, [data, form, dispatch]);

  useEffect(() => {
    if (updated) {
      toast("success", "Vendor updated successfully.");
      setIsEditMode(false);
    }
  }, [updated]);

  useError(isUpdateError, updateError, "Vendor update fail.");

  // Segment configuration
  const segmentConfig: {
    label: string;
    value: SegmentKey;
    view: React.ReactNode;
    edit?: React.ReactNode;
  }[] = [
    {
      label: "General",
      value: "general",
      view: <GeneralDetails data={general} />,
      edit: <General form={form} />,
    },
    {
      label: "Contacts",
      value: "contacts",
      view: <ContactDetails data={contacts} />,
      edit: <Contacts form={form} />,
    },
    {
      label: "Payment",
      value: "payments",
      view: <PaymentDetails data={payments} />,
      edit: <Payments form={form} />,
    },
    {
      label: "Documents",
      value: "documents",
      view: <DocumentDetails data={documents} />,
      edit: <Documents form={form} />,
    },
    {
      label: "Activity Log",
      value: "activity-log",
      view: <ActivityLog />,
    },
  ];

  const currentSegment = segmentConfig.find((s) => s.value === activeSegment);

  return (
    <SidebarLayout title="Vendor Management">
      <TitleBar
        backLink="/admin/vendors"
        image={(general as TVendorDetails)?.logo}
        title={(general as TVendorDetails)?.business_name}
        subTitle={`ID: ${(general as TVendorDetails)?.vendor_code}`}
      >
        {!isEditMode && (
          <Button
            onClick={() => setIsEditMode(true)}
            className="button-white-bordered"
          >
            <PencilLine /> Edit
          </Button>
        )}
      </TitleBar>

      <Segmented
        options={segmentConfig.map(({ label, value }) => ({ label, value }))}
        value={activeSegment}
        onChange={(value) => {
          setActiveSegment(value as SegmentKey);
          setIsEditMode(false);
        }}
        size="large"
        shape="round"
        className="custom-segmented"
      />

      <WithLoader isLoading={isLoading || isFetching}>
        <div className="mt-5">
          {isEditMode && currentSegment?.edit
            ? currentSegment.edit
            : currentSegment?.view}
        </div>
      </WithLoader>

      {isEditMode && (
        <Flex justify="space-between" align="center" className="mt-6">
          <Button
            onClick={() => setIsEditMode(false)}
            className="button-white-bordered min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            loading={updating}
            onClick={handleAction}
            className="button-primary min-w-[100px]"
          >
            Update
          </Button>
        </Flex>
      )}
    </SidebarLayout>
  );
};

export default VendorDetails;
